import { AnimatePresence, motion } from 'framer-motion';
import type { ReactNode } from 'react';

import { cn } from '@/lib/shadcn/utils';

interface PreviewFrameProps {
  label: string;
  children: ReactNode;
  className?: string;
}

/** A quiet framed preview with a caption. */
export const PreviewFrame = ({
  label,
  children,
  className,
}: PreviewFrameProps) => (
  <figure className={cn('flex flex-col gap-2', className)}>
    <figcaption className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">
      {label}
    </figcaption>
    <div className="bg-muted/60 rounded-lg border p-3 sm:p-4">{children}</div>
  </figure>
);

interface SlackMessagePreviewProps {
  /** Channel shown in the header line; direct messages when omitted. */
  channelName?: string;
  /**
   * The signed-in user, greeted the way the real intro greets them. Blank
   * names fall back like finlay-api's `GetRecipientFirstName`.
   */
  firstName?: string;
}

const Divider = () => <hr className="my-2" />;

/**
 * The candidate intro Finlay posts once connected, mirroring
 * `CandidateIntroductionMessage.BuildContent` in finlay-api: a channel post
 * tags the hiring manager, a DM greets them by name. Static sample content;
 * the interview-questions section is left out to fit the narrow column.
 */
export const SlackMessagePreview = ({
  channelName,
  firstName,
}: SlackMessagePreviewProps) => (
  <div className="rounded-md border bg-white text-[13px] shadow-xs">
    <div className="text-muted-foreground flex items-center gap-1.5 border-b px-3 py-1.5 text-xs">
      <AnimatePresence mode="wait" initial={false}>
        <motion.span
          key={channelName ?? 'dm'}
          initial={{ opacity: 0, y: 3 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -3 }}
          transition={{ duration: 0.18 }}
          className="text-primary-700 font-medium"
        >
          {channelName ? `#${channelName}` : 'Finlay app DM'}
        </motion.span>
      </AnimatePresence>
    </div>
    <div className="flex gap-2.5 px-3 py-2.5">
      {/* The Finlay Slack app's icon; decorative beside the "Finlay" name. */}
      <img
        src="/finlay-slack-app-icon.png"
        alt=""
        className="size-8 shrink-0 rounded-md"
      />
      <div className="min-w-0 flex-1 leading-[18px]">
        <p className="flex items-baseline gap-1.5">
          <span className="font-semibold">Finlay</span>
          <span className="text-muted-foreground rounded-sm bg-neutral-200 px-1 text-[10px] font-medium">
            APP
          </span>
          <span className="text-muted-foreground text-xs">9:41</span>
        </p>
        <p className="mt-1 text-[15px] font-bold">Introducing Sam Taylor</p>
        <p className="mt-1">
          {channelName ? (
            <span className="rounded-sm bg-blue-50 px-0.5 text-blue-700">
              @{firstName?.trim() || 'you'}
            </span>
          ) : (
            `Hey ${firstName?.trim() || 'there'},`
          )}{' '}
          I recommend meeting Sam Taylor. Built reliable APIs and cut database
          load at Acme.
        </p>
        <p className="mt-2 font-semibold">Sam Taylor</p>
        <p className="text-muted-foreground">Backend Engineer · Prague</p>
        <Divider />
        <p className="font-semibold">Why Sam Taylor is worth meeting</p>
        <p className="mt-1">
          API design: led the public API partner teams build on.
        </p>
        <Divider />
        <p className="font-semibold">Verified in pre-screen</p>
        <ul className="mt-1">
          <li>• Available to start in 4 weeks</li>
          <li>• Open to hybrid work in Prague</li>
        </ul>
        <p className="mt-1 text-blue-700">Read pre-screening notes</p>
        <span className="mt-2 inline-block rounded-md bg-green-700 px-2.5 py-1 text-xs font-semibold text-white">
          Review Candidate
        </span>
      </div>
    </div>
  </div>
);
